/**
 * StatsPills - Status filter pills for the Overview page
 *
 * Displays clickable stat cards for filtering photos by status.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import IngestionFailureList from '../../shared/components/IngestionFailureList';

// Status pill configuration with colors and labels
// Order: Total, Done, Review, Tagging, Problems, Skipped (left-to-right, action pills first)
const PILL_CONFIG = [
  { key: null, label: "Total", getValue: (t) => t.total, color: "#66ffb7" },
  { key: "done", label: "Done", getValue: (t) => t.doneTotal, color: "#66ffb7" },
  { key: "review", label: "Review", getValue: (t) => t.review || 0, color: "#66d9e8" },
  { key: "tagging", label: "Tagging", getValue: (t) => t.tagging || 0, color: "#ff9b64" },
  { key: "problems", label: "Problems", getValue: (t) => (t.attention || 0) + (t.error || 0), color: "#ff6b6b" },
  { key: "skipped", label: "Skipped", getValue: (t) => t.skipped || 0, color: "#888888" },
];

// Pipeline status cards (non-clickable, with progress stripes)
// "ingested" is now handled separately as the smart Ingest pill
const PIPELINE_PILLS = [
  { key: "processing", label: "Processing", stageKey: "pipeline" },
];

/**
 * Determine the color for the ingest pill value based on health.
 */
function getIngestColor(ingestionStats) {
  if (!ingestionStats) return '#66ffb7';
  const { received, ingested, failed } = ingestionStats;
  // Red: >10% failure rate
  if (received > 0 && failed > 0 && failed / received > 0.1) return '#ff6b6b';
  // Orange: any failures or duplicates
  if (failed > 0 || (ingestionStats.duplicate || 0) > 0) return '#ff9b64';
  // Green: all healthy
  if (received === ingested) return '#66ffb7';
  return '#66ffb7';
}

function StatsPills({ totals, activeFilters, onToggle, onClear, metrics: _metrics, ingestionStats }) {
  const [ingestExpanded, setIngestExpanded] = useState(false);
  const [showFailureList, setShowFailureList] = useState(false);
  const collapseTimerRef = useRef(null);

  const handleCardKey = (handler) => (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handler();
    }
  };

  const renderStripe = (stageKey, label, activeCount) => {
    if (activeFilters.length) return null;
    if (!activeCount) return null;
    // Pulsing indicator to show activity (static key prevents animation restart)
    return (
      <div
        className="card-fill card-fill-pulse"
        key={stageKey}
        aria-label={`${label} in progress`}
      />
    );
  };

  // Auto-hide expanded detail after 5 seconds
  useEffect(() => {
    if (ingestExpanded) {
      collapseTimerRef.current = setTimeout(() => {
        setIngestExpanded(false);
      }, 5000);
      return () => clearTimeout(collapseTimerRef.current);
    }
    return undefined;
  }, [ingestExpanded]);

  const toggleIngestDetail = useCallback(() => {
    setIngestExpanded((prev) => !prev);
  }, []);

  // Determine ingest pill values
  const hasIngestionData = ingestionStats && typeof ingestionStats.received === 'number';
  const ingestReceived = hasIngestionData ? ingestionStats.received : null;
  const ingestIngested = hasIngestionData ? ingestionStats.ingested : null;
  const ingestFailed = hasIngestionData ? (ingestionStats.failed || 0) : 0;
  const ingestDuplicate = hasIngestionData ? (ingestionStats.duplicate || 0) : 0;
  const ingestColor = getIngestColor(ingestionStats);
  // Pipeline is active when received > ingested (still processing)
  const ingestActive = hasIngestionData && ingestReceived > ingestIngested;

  return (
    <>
      <div className="stat-grid">
        {/* Clickable filter pills */}
        {PILL_CONFIG.map((pill) => {
          const isActive = pill.key === null
            ? activeFilters.length === 0
            : activeFilters.includes(pill.key);
          const onClick = pill.key === null ? onClear : () => onToggle(pill.key);

          return (
            <div
              key={pill.key || "total"}
              className={`stat-card ${isActive ? "active" : ""}`}
              role="button"
              tabIndex={0}
              onClick={onClick}
              onKeyDown={handleCardKey(onClick)}
              style={{ "--stat-color": pill.color, cursor: "pointer" }}
            >
              <div className="label">{pill.label}</div>
              <div className="value">{pill.getValue(totals)}</div>
            </div>
          );
        })}

        {/* Pipeline status pills (non-clickable, with progress stripes) */}
        {PIPELINE_PILLS.map((pill) => (
          <div key={pill.key} className="stat-card">
            <div className="label">{pill.label}</div>
            <div className="value">{totals[pill.key] || 0}</div>
            {renderStripe(pill.stageKey, pill.label, totals[pill.key])}
          </div>
        ))}

        {/* Smart Ingest pill */}
        <div
          className="stat-card"
          role="button"
          tabIndex={0}
          onClick={toggleIngestDetail}
          onKeyDown={handleCardKey(toggleIngestDetail)}
          aria-expanded={ingestExpanded}
          style={{ cursor: "pointer" }}
        >
          <div className="label">Ingest</div>
          <div className="value" style={{ color: ingestColor }}>
            {hasIngestionData
              ? `${ingestIngested} / ${ingestReceived}`
              : (totals.ingested || 0)}
          </div>
          {ingestActive && renderStripe("merovingian", "Ingest", true)}
        </div>
      </div>

      {/* Expanded ingestion detail bar */}
      {ingestExpanded && hasIngestionData && (
        <div
          style={{
            display: 'flex',
            gap: 24,
            padding: '8px 16px',
            marginTop: -6,
            marginBottom: 14,
            background: 'rgba(10, 19, 26, 0.8)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontSize: 13,
            color: 'var(--muted)',
          }}
        >
          <span>Received: <strong style={{ color: '#ccc' }}>{ingestReceived}</strong></span>
          <span>Ingested: <strong style={{ color: '#66ffb7' }}>{ingestIngested}</strong></span>
          <span
            onClick={(e) => { e.stopPropagation(); if (ingestFailed > 0) setShowFailureList((v) => !v); }}
            style={{ cursor: ingestFailed > 0 ? 'pointer' : 'default', textDecoration: ingestFailed > 0 ? 'underline' : 'none' }}
            title={ingestFailed > 0 ? 'Click to see failed files' : ''}
          >
            Failed: <strong style={{ color: ingestFailed > 0 ? '#ff6b6b' : '#ccc' }}>{ingestFailed}</strong>
          </span>
          <span>Duplicates: <strong style={{ color: ingestDuplicate > 0 ? '#ff9b64' : '#ccc' }}>{ingestDuplicate}</strong></span>
        </div>
      )}

      {/* Per-file failure details */}
      {showFailureList && (
        <IngestionFailureList onClose={() => setShowFailureList(false)} />
      )}
    </>
  );
}

export default StatsPills;
